import type { CaseForPharmacy, CaseSummary, PatientView } from "@omony/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../shared/api.js";
import { queryClient } from "../shared/query.js";

/** Datenzugriff der Apotheken-Konsole (TanStack Query, Invalidierung per SSE). */

export interface DeviceInfo {
  device: { id: string; kind: "kiosk" | "signage"; name: string };
  pharmacy: { name: string; city: string };
  staff: Array<{ id: string; displayName: string }>;
  csrfToken: string;
  lockSeconds: number;
  unlocked: { staffMemberId: string; displayName: string } | null;
}

export interface Features {
  smed: { enabled: boolean; embedMode: "iframe" | "redirect" | null; supportedLanguages: string[] };
  ocrVision: boolean;
  video: { provider: "meetone" | "jitsi_dev"; devOnly: boolean };
  rules: {
    maxActiveCasesPerDoctor: number;
    queueWarnMinutes: number;
    deviceLockSeconds: number;
    billingFlatFeeCents: number;
  };
  serverTime: string;
}

export interface SmedView {
  id: string;
  status: "erstellt" | "laufend" | "abgeschlossen" | "abgebrochen" | "fehlgeschlagen" | "abgelaufen";
  embedMode: "iframe" | "redirect" | null;
  verified: boolean;
  result: null | {
    pointOfCareCode: string;
    pointOfCareText: string;
    timeToTreatCode: string;
    timeToTreatText: string;
    mainSymptom: string;
    relatedSymptoms: string[];
    mapping: { result: "geeignet" | "notfall"; urgency: "rot" | "gelb" | "gruen"; emergency: boolean };
  };
}

export const useDevice = (enabled: boolean) =>
  useQuery({
    queryKey: ["device"],
    queryFn: () => api<DeviceInfo>("/device", { background: true }),
    enabled,
    staleTime: 60_000,
  });

export const useFeatures = () =>
  useQuery({
    queryKey: ["features"],
    queryFn: () => api<Features>("/features", { background: true }),
    staleTime: 300_000,
  });

export const useCase = (id: string | null) =>
  useQuery({
    queryKey: ["case", id],
    queryFn: () => api<CaseForPharmacy>(`/cases/${id}`),
    enabled: !!id,
  });

export const useOpenCases = () =>
  useQuery({
    queryKey: ["cases", "open"],
    queryFn: () => api<{ items: CaseSummary[] }>("/cases?scope=open", { background: true }),
  });

export const usePatientView = (enabled: boolean) =>
  useQuery({
    queryKey: ["patient-view"],
    queryFn: () =>
      api<{ case: PatientView | null; serverTime: string }>("/device/patient-view", { background: true }),
    enabled,
    refetchInterval: 30_000,
  });

export const useSmed = (caseId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["smed", caseId],
    queryFn: () => api<SmedView>(`/cases/${caseId}/triage/smed`, { background: true }),
    enabled,
    // Rückfall, falls ein Ereignis verloren geht (primär kommt triage.updated per SSE)
    refetchInterval: (q) => (q.state.data?.status === "laufend" ? 5000 : false),
  });

/** Antwort einer Fall-Aktion übernehmen (enthält `case` oder zumindest den neuen Status) */
export function applyCaseResponse(id: string, r: unknown) {
  const c = (r as { case?: CaseForPharmacy } | undefined)?.case;
  if (c) queryClient.setQueryData(["case", id], c);
  void queryClient.invalidateQueries({ queryKey: ["case", id] });
  void queryClient.invalidateQueries({ queryKey: ["cases"] });
}

/** Fall-Aktion als Mutation: POST/PUT an /cases/:id/<path> */
export function useCaseAction<B = unknown>(
  id: string,
  path: string,
  method: "POST" | "PUT" | "DELETE" = "POST",
) {
  return useMutation({
    mutationFn: (body?: B) =>
      api<unknown>(`/cases/${id}${path ? `/${path}` : ""}`, { method, body: body ?? {} }),
    onSuccess: (r) => applyCaseResponse(id, r),
  });
}
